// src/features/atividade/geo.js
//
// A aritmética da gravação: o que é ponto bom, quanto se andou, em que ritmo,
// quanto subiu. Módulo PURO — nenhuma chamada de plugin, nenhum Firestore,
// nenhum React. É por isso que dá para testar com trajeto sintético
// (geo.test.js) sem emulador e sem sair correndo na rua.
//
// FORMA DO PONTO, uma só no arquivo inteiro:
//   { lat, lng, alt, t, acc, v }
//   t   = ms desde a época (o `time` do provedor, não Date.now() do app)
//   acc = erro horizontal em metros, 68% de confiança
//   alt = metros acima do nível do mar, ou null
//   v   = m/s do provedor, ou null (aí é derivada)
//
// O QUE ESTE ARQUIVO NÃO FAZ: chamar Elevation API. O desnível sai da altitude
// do próprio GPS, suavizada. É menos exato e é de graça; uma API paga por
// ponto, numa feature cujo objetivo é ser usada todo dia, é custo variável
// preso ao uso.

import { PRECISAO_MAX_M, JANELA_PACE_M } from './atividadeConfig.js';
import { estimarCalorias } from '../treinos/utils/pointsConfig.js';

const RAIO_TERRA_M = 6371008.8; // raio médio (IUGG), o que o Haversine pede
const grau = (x) => (x * Math.PI) / 180;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Este valor é um número de verdade?
 *
 * `Number.isFinite(Number(x))` sozinho NÃO serve, e o erro é silencioso e caro:
 * `Number(null)` é 0, e 0 é finito. Sem esta guarda, um ponto sem precisão
 * (`acc: null`, que é o que o provedor manda quando não sabe) passaria como
 * "precisão perfeita de 0 m", e uma altitude ausente viraria "nível do mar" —
 * um paredão de 700 m no meio do gráfico de elevação de São Paulo.
 */
export const finito = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

// ── Distância ────────────────────────────────────────────────────────────────

/**
 * Haversine entre dois pontos, em metros.
 *
 * Haversine e não Vincenty: a diferença entre a esfera e o elipsoide é ~0,3%,
 * bem abaixo do erro do próprio GPS urbano, e Vincenty tem caso de não
 * convergência em pontos antipodais que ninguém quer depurar no meio de uma
 * corrida.
 *
 * @param {object} a ponto {lat, lng}
 * @param {object} b ponto {lat, lng}
 * @returns {number} metros
 */
export function haversine(a, b) {
  if (!a || !b) return 0;
  const dLat = grau(num(b.lat) - num(a.lat));
  const dLng = grau(num(b.lng) - num(a.lng));
  const lat1 = grau(num(a.lat));
  const lat2 = grau(num(b.lat));

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * RAIO_TERRA_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

// ── Quebra de trecho ─────────────────────────────────────────────────────────
//
// `q: 1` no ponto quer dizer "este ponto ABRE um trecho novo": o segmento que
// chega nele, vindo do ponto anterior, não foi percorrido em atividade. É o
// primeiro ponto depois de uma pausa (manual ou por veículo) e depois de uma
// gravação recuperada.
//
// Sem a marca, a soma de Haversine ligava o último ponto ANTES da pausa ao
// primeiro DEPOIS dela — e quem pausou, pegou um ônibus por 10 km e retomou
// ganhava 10 km de corrida em linha reta. A linha do traçado continua ligando
// os dois (o mapa não tem buraco); o que a marca tira é a DISTÂNCIA, o tempo do
// split e a subida daquele segmento.
//
// Um campo de uma letra porque viaja em cada ponto do buffer local.

/** Este ponto abre um trecho novo (o segmento que chega nele não conta)? */
export const quebraAntes = (p) => p?.q === 1;

/**
 * Distância percorrida, em metros, respeitando as quebras de trecho.
 *
 * @param {Array} pontos pontos aceitos, em ordem
 * @returns {number} metros
 */
export function distanciaTotal(pontos) {
  if (!Array.isArray(pontos)) return 0;
  let total = 0;
  for (let i = 1; i < pontos.length; i += 1) {
    if (!quebraAntes(pontos[i])) total += haversine(pontos[i - 1], pontos[i]);
  }
  return total;
}

// ── Filtro ───────────────────────────────────────────────────────────────────

/**
 * Motivos de descarte. String, não booleano, porque quem chama precisa saber
 * POR QUE o ponto caiu — "GPS ruim" acende o banner de precisão, "repetido"
 * alimenta o auto-pause, e "salto" não deve fazer nenhuma das duas coisas.
 */
export const DESCARTE = {
  PRECISAO: 'PRECISAO',
  SALTO: 'SALTO',
  REPETIDO: 'REPETIDO',
  RETROCEDE: 'RETROCEDE',
};

/**
 * Este ponto entra no traçado?
 *
 * Quatro recusas, nesta ordem:
 *  1. precisão pior que 30 m — o ponto é um círculo de 30 m de raio, e somar
 *     a distância entre dois círculos desses inventa metro que ninguém correu;
 *  2. tempo que não anda (ou anda para trás) — provedor repetindo o mesmo fix;
 *  3. movimento menor que o erro dos dois pontos E provedor sem dizer que a
 *     pessoa anda — ruído de quem está parado;
 *  4. salto implausível — velocidade acima do teto da modalidade só acontece
 *     quando o GPS reengata depois de um túnel e "teleporta" a pessoa.
 *
 * @param {object|null} anterior último ponto ACEITO, ou null no primeiro
 * @param {object} ponto candidato
 * @param {object} opcoes `{ velMaxMs, precisaoMaxM, paradoMs }`
 * @returns {{ok: boolean, motivo?: string, distancia: number, dt: number}}
 */
export function aceitarPonto(anterior, ponto, opcoes = {}) {
  const precisaoMax = num(opcoes.precisaoMaxM) || PRECISAO_MAX_M;
  const velMax = num(opcoes.velMaxMs) || Infinity;
  // Abaixo disto o provedor está dizendo "parado". Cai em 0,5 m/s (1,8 km/h,
  // menos que qualquer caminhada) quando o chamador não informa a modalidade.
  const paradoMs = num(opcoes.paradoMs) || 0.5;

  if (!ponto || !finito(ponto.lat) || !finito(ponto.lng)) {
    return { ok: false, motivo: DESCARTE.PRECISAO, distancia: 0, dt: 0 };
  }
  // `acc` ausente não é `acc` bom: sem saber a precisão, o ponto não passa.
  if (!finito(ponto.acc) || Number(ponto.acc) > precisaoMax) {
    return { ok: false, motivo: DESCARTE.PRECISAO, distancia: 0, dt: 0 };
  }
  if (!anterior) return { ok: true, distancia: 0, dt: 0 };

  const dt = (num(ponto.t) - num(anterior.t)) / 1000;
  if (dt <= 0) {
    return { ok: false, motivo: DESCARTE.RETROCEDE, distancia: 0, dt };
  }

  const d = haversine(anterior, ponto);

  // Ruído de parado. Sem isto, ficar de pé num semáforo "anda" 200 m em três
  // minutos: cada fix cai num lugar diferente dentro do círculo de erro e a
  // soma dos saltinhos vira distância. O piso de 3 m existe porque um GPS que
  // reporta acc=1 m está otimista, não perfeito.
  //
  // ── MAS O PISO NÃO VALE CONTRA QUEM ESTÁ ANDANDO ──────────────────────────
  //
  // `acc` é o erro ABSOLUTO da posição; o que se mede aqui é o deslocamento
  // RELATIVO entre dois fixes seguidos, que é muito mais preciso — o erro de
  // GPS é correlacionado entre fixes vizinhos (mesma geometria de satélites,
  // mesmo multicaminho). Comparar um com o outro fazia a régua crescer com a
  // precisão: com `acc` de 25 m, típico de rua com prédio alto, e um fix por
  // segundo, TODO passo de quem corre a 3 m/s caía como ruído.
  //
  // O estrago não era só a linha ficar para trás do ponto no mapa (o marcador
  // vem de todo fix, o traçado só dos aceitos): a recusa alimenta o
  // auto-pause, então o app pausava sozinho quem estava correndo, e o tempo
  // "em movimento" virava um punhado de segundos no fim de 45 minutos de
  // corrida.
  //
  // Quem desempata é a velocidade do PROVEDOR, que no iOS vem do Doppler e
  // não de subtrair duas posições. Dizendo ela que a pessoa anda, o ponto
  // entra. Sem velocidade (`null`, ou o -1 que o iOS manda quando não sabe) a
  // régua antiga continua valendo inteira.
  const ruido = Math.max(3, (num(anterior.acc) + num(ponto.acc)) / 2);
  const andando = finito(ponto.v) && Number(ponto.v) >= paradoMs;
  if (d < ruido && !andando) {
    return { ok: false, motivo: DESCARTE.REPETIDO, distancia: 0, dt };
  }

  if (d / dt > velMax) return { ok: false, motivo: DESCARTE.SALTO, distancia: 0, dt };

  return { ok: true, distancia: d, dt };
}

// ── Pace ─────────────────────────────────────────────────────────────────────

/**
 * Ritmo instantâneo: média móvel dos últimos 200 m (brief §6.7).
 *
 * Por que janela por DISTÂNCIA e não por tempo: o número que a pessoa lê tem
 * de ser "meu ritmo agora", e uma janela por tempo com a pessoa parada no
 * farol dilui o ritmo até virar mentira. Por distância, parar congela a
 * janela em vez de envenená-la.
 *
 * @param {Array} pontos pontos ACEITOS, em ordem
 * @param {number} janelaM janela em metros
 * @returns {number|null} segundos por quilômetro, ou null sem janela suficiente
 */
export function paceMovel(pontos, janelaM = JANELA_PACE_M) {
  if (!Array.isArray(pontos) || pontos.length < 2) return null;

  let dist = 0;
  let i = pontos.length - 1;
  // A janela não atravessa uma quebra de trecho: do outro lado está o tempo da
  // pausa, e ele entraria no ritmo como se fosse corrida lenta.
  while (i > 0 && dist < janelaM && !quebraAntes(pontos[i])) {
    dist += haversine(pontos[i - 1], pontos[i]);
    i -= 1;
  }
  if (dist <= 0) return null;
  // Janela incompleta no começo da atividade: com menos de um quarto dela o
  // número pula demais para valer a pena mostrar.
  if (dist < janelaM / 4) return null;

  const segundos = (num(pontos[pontos.length - 1].t) - num(pontos[i].t)) / 1000;
  if (segundos <= 0) return null;

  return (segundos / dist) * 1000;
}

/** Ritmo médio da atividade inteira, em segundos por km. */
export function paceMedio(distanciaM, duracaoS) {
  const d = num(distanciaM);
  const s = num(duracaoS);
  if (d <= 0 || s <= 0) return null;
  return (s / d) * 1000;
}

/** `5'02"` a partir de segundos por km. Null vira o traço do protótipo. */
export function formatarPace(segundosPorKm) {
  const v = Number(segundosPorKm);
  if (!Number.isFinite(v) || v <= 0) return '--\'--"';
  const total = Math.round(v);
  const min = Math.floor(total / 60);
  const seg = total % 60;
  return `${min}'${String(seg).padStart(2, '0')}"`;
}

/** `24:18` ou `1:04:07`. */
export function formatarTempo(segundos) {
  const total = Math.max(0, Math.floor(num(segundos)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const dois = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${dois(m)}:${dois(s)}` : `${dois(m)}:${dois(s)}`;
}

/** Velocidade média em km/h — o que a bike mostra no lugar do pace. */
export function velocidadeKmh(distanciaM, duracaoS) {
  const s = num(duracaoS);
  if (s <= 0) return 0;
  return (num(distanciaM) / s) * 3.6;
}

// ── Elevação ─────────────────────────────────────────────────────────────────

/** Variação de altitude abaixo disto é ruído do GPS, não ladeira. */
const LIMIAR_ELEVACAO_M = 3;
/** Sobra menor que isto não vira split parcial. */
const SOBRA_MINIMA_M = 50;

/** Tamanho da janela da média que suaviza a altitude (ímpar, centrada). */
const JANELA_SUAVIZACAO = 5;

/**
 * Suaviza a série de altitudes com média móvel centrada.
 *
 * A altitude do GPS oscila metros entre fixes consecutivos com o aparelho
 * parado — o erro vertical é tipicamente 1,5× o horizontal. Somar essa
 * oscilação como subida dá 300 m de desnível numa volta no quarteirão plano.
 *
 * @param {Array<number|null>} alts
 * @returns {Array<number|null>}
 */
export function suavizarAltitude(alts) {
  if (!Array.isArray(alts) || !alts.length) return [];
  const meia = Math.floor(JANELA_SUAVIZACAO / 2);
  return alts.map((_, i) => {
    let soma = 0;
    let n = 0;
    for (let j = i - meia; j <= i + meia; j += 1) {
      const v = alts[j];
      if (finito(v)) {
        soma += Number(v);
        n += 1;
      }
    }
    return n ? soma / n : null;
  });
}

/**
 * Desnível acumulado, positivo e negativo, em metros.
 *
 * `barometro` entra na frente quando existe: o barômetro mede pressão, não
 * posição, então não herda o erro vertical do GPS. Hoje nenhum plugin do app
 * expõe barômetro — o parâmetro existe para o dia em que expuser, e até lá o
 * caminho é a altitude do GPS suavizada.
 *
 * @param {Array} pontos pontos aceitos
 * @param {Array<number>|null} barometro altitudes barométricas alinhadas a `pontos`
 * @returns {{pos: number, neg: number}} metros
 */
export function elevacao(pontos, barometro = null) {
  const lista = Array.isArray(pontos) ? pontos : [];
  const serie =
    Array.isArray(barometro) && barometro.length === lista.length
      ? barometro
      : lista.map((p) => (finito(p?.alt) ? Number(p.alt) : null));

  // Um trecho por vez: subir a serra de carro com a gravação pausada e retomar
  // lá em cima não é desnível de quem corre.
  let pos = 0;
  let neg = 0;
  let inicio = 0;
  for (let i = 1; i <= lista.length; i += 1) {
    if (i === lista.length || quebraAntes(lista[i])) {
      const trecho = desnivelDoTrecho(serie.slice(inicio, i));
      pos += trecho.pos;
      neg += trecho.neg;
      inicio = i;
    }
  }

  return { pos: Math.round(pos), neg: Math.round(neg) };
}

function desnivelDoTrecho(serie) {
  const suave = suavizarAltitude(serie).filter((v) => finito(v));
  if (suave.length < 2) return { pos: 0, neg: 0 };

  let pos = 0;
  let neg = 0;
  let referencia = suave[0];

  for (let i = 1; i < suave.length; i += 1) {
    const delta = suave[i] - referencia;
    // A referência só se move quando o deslocamento passa do limiar. Entre um
    // limiar e outro ela fica parada — é isso que impede a oscilação de virar
    // subida e descida alternadas somando as duas.
    if (delta >= LIMIAR_ELEVACAO_M) {
      pos += delta;
      referencia = suave[i];
    } else if (delta <= -LIMIAR_ELEVACAO_M) {
      neg += -delta;
      referencia = suave[i];
    }
  }

  return { pos, neg };
}

// ── Calorias ─────────────────────────────────────────────────────────────────

/**
 * kcal = MET × peso × horas, pela MESMA função que o check-in manual usa
 * (`estimarCalorias` de pointsConfig). Duas fórmulas de caloria no app seriam
 * duas respostas para a mesma pergunta, e a barra "Calorias da semana" somaria
 * maçã com laranja.
 *
 * @param {object} args `{ activityKey, duracaoS, pesoKg }`
 * @returns {{kcal: number, met: number, aproximado: boolean}}
 */
export function calorias({ activityKey, duracaoS, pesoKg } = {}) {
  return estimarCalorias({
    key: activityKey,
    durationMin: num(duracaoS) / 60,
    pesoKg,
  });
}

// ── Passos ───────────────────────────────────────────────────────────────────

/** Cadência típica, em passos por metro, por modalidade que tem passo. */
const PASSOS_POR_METRO = { corrida: 1.25, caminhada: 1.35, trilha: 1.4 };

/**
 * Estimativa de passos. Só para as modalidades a pé, e declaradamente
 * estimativa: o app não tem pedômetro (nenhum plugin de Motion instalado) e
 * `pointsConfig` dá peso a `steps` na corrida, na caminhada e na trilha. Zero
 * ali subestimaria os pontos de quem caminhou 10 km.
 *
 * @param {string} modalidadeId
 * @param {number} distanciaM
 * @returns {number} passos
 */
export function estimarPassos(modalidadeId, distanciaM) {
  const fator = PASSOS_POR_METRO[modalidadeId];
  if (!fator) return 0;
  return Math.round(num(distanciaM) * fator);
}

// ── Splits ───────────────────────────────────────────────────────────────────

/**
 * Parciais por quilômetro.
 *
 * O ponto exato do quilômetro cai NO MEIO de um segmento quase sempre, então o
 * instante é interpolado linearmente dentro do segmento que cruza a marca — sem
 * isso, um segmento de 40 m jogaria até 40 m de erro para o split seguinte, e o
 * erro se acumularia km a km.
 *
 * O último split é PARCIAL quando a atividade não fecha o km, vem marcado
 * (`parcial: true`) e com o pace NORMALIZADO por km — senão a barra de
 * comparação da tela de detalhe mostraria a sobra de 300 m como "o km mais
 * rápido da vida" só por ser curta.
 *
 * @param {Array} pontos pontos aceitos
 * @returns {Array<{km:number,paceS:number,eleM:number,distanciaM:number,parcial:boolean}>}
 */
export function calcularSplits(pontos) {
  if (!Array.isArray(pontos) || pontos.length < 2) return [];

  const splits = [];
  let acumulado = 0; // metros desde o início
  let marcaDist = 0; // metros no começo do split corrente
  let marcaT = num(pontos[0].t);
  let marcaAlt = finito(pontos[0].alt) ? Number(pontos[0].alt) : null;

  for (let i = 1; i < pontos.length; i += 1) {
    const a = pontos[i - 1];
    const b = pontos[i];

    // Segmento de quebra (pausa, veículo): nem distância nem tempo. O relógio
    // do split corrente é empurrado para a frente pelo tanto que a pausa
    // durou — senão o km em que a pessoa pausou sairia com o pace da pausa.
    if (quebraAntes(b)) {
      marcaT += Math.max(0, num(b.t) - num(a.t));
      // A altitude também recomeça do lado de cá: a diferença de altura do
      // trajeto de carro não é subida do split.
      const altB = finito(b.alt) ? Number(b.alt) : null;
      if (marcaAlt != null && altB != null && finito(a.alt)) {
        marcaAlt += altB - Number(a.alt);
      }
      continue;
    }

    const d = haversine(a, b);
    if (d <= 0) continue;

    const ta = num(a.t);
    const tb = num(b.t);
    const altA = finito(a.alt) ? Number(a.alt) : null;
    const altB = finito(b.alt) ? Number(b.alt) : null;

    // O começo DESTE segmento, e ele não se move: a fração interpolada é
    // sempre relativa ao segmento INTEIRO, porque `ta`/`tb` são as pontas do
    // segmento inteiro. Mover a referência a cada km cruzado — como seria
    // natural escrever — faz o segundo km de um segmento longo sair com metade
    // do tempo certo, e o erro só aparece em trajeto com fix esparso (túnel,
    // app em segundo plano), que é justamente onde ninguém vai conferir.
    const segIni = acumulado;
    acumulado += d;

    // Um segmento longo pode cruzar mais de uma marca de km.
    // O epsilon é contra o próprio ponto flutuante: somar 300 haversines de
    // 10 m dá 2999,9999999998, e sem folga um trajeto que fecha exatamente em
    // 3 km sairia com dois splits cheios e um parcial de 999,99 m.
    const EPS = 1e-6;
    while (Math.floor((acumulado + EPS) / 1000) > Math.floor(marcaDist / 1000)) {
      const alvo = (Math.floor(marcaDist / 1000) + 1) * 1000;
      const fracao = Math.min(1, (alvo - segIni) / d);
      const tAlvo = ta + (tb - ta) * fracao;
      const altAlvo =
        altA != null && altB != null ? altA + (altB - altA) * fracao : altB != null ? altB : altA;

      splits.push({
        km: Math.floor(alvo / 1000),
        paceS: Math.round((tAlvo - marcaT) / 1000),
        eleM: marcaAlt != null && altAlvo != null ? Math.round(altAlvo - marcaAlt) : 0,
        distanciaM: 1000,
        parcial: false,
      });

      marcaDist = alvo;
      marcaT = tAlvo;
      marcaAlt = altAlvo;
    }
  }

  // Sobra: o pedaço depois do último km cheio. Menos de 50 m não é split, é
  // resto de arredondamento — mostrá-lo encheria a lista de parciais de 8 m.
  const sobra = acumulado - marcaDist;
  if (sobra >= SOBRA_MINIMA_M) {
    const ultimo = pontos[pontos.length - 1];
    const altFim = finito(ultimo.alt) ? Number(ultimo.alt) : null;
    const segundos = (num(ultimo.t) - marcaT) / 1000;
    splits.push({
      km: splits.length + 1,
      paceS: Math.round((segundos / sobra) * 1000),
      eleM: marcaAlt != null && altFim != null ? Math.round(altFim - marcaAlt) : 0,
      distanciaM: Math.round(sobra),
      parcial: true,
    });
  }

  return splits;
}

// ── Resumo ───────────────────────────────────────────────────────────────────

/**
 * Tudo o que a tela e o documento precisam, a partir dos pontos aceitos.
 *
 * `duracaoS` é o tempo em movimento: o decorrido menos as pausas (manuais e
 * automáticas) que o chamador contabilizou. Um treino de 40 min com 10 min de
 * farol vermelho é um treino de 30 min de esforço, e é isso que vira ponto, XP
 * e caloria.
 *
 * ── O DECORRIDO É O DO RELÓGIO, NÃO O DOS PONTOS ────────────────────────────
 *
 * `inicio` e `fim` são os instantes em que a gravação começou e terminou, e o
 * chamador os informa. Medir o decorrido pelo primeiro e pelo último ponto
 * ACEITO descontava a pausa duas vezes: quem para de andar para de gerar ponto
 * aceito, então o intervalo entre os pontos JÁ não contém a parada — e o
 * `pausaS` a tirava de novo. Numa corrida que termina com a pessoa parada (o
 * comum: ela para, respira, e só então procura o botão), o resultado era
 * `max(0, poucos minutos - muitos minutos)` = zero, e com ele o pace em
 * `--'--`, a caloria em 0 e o XP recusado pelo gate de minutos do servidor.
 *
 * Sem `inicio`/`fim` o cálculo cai nos carimbos dos pontos, que é tudo o que
 * existe quando o resumo é refeito a partir do buffer recuperado.
 *
 * @param {object} args `{ pontos, modalidadeId, pesoKg, pausaS, inicio, fim }`
 * @returns {object} resumo
 */
export function resumir({
  pontos = [],
  modalidadeId,
  pesoKg,
  pausaS = 0,
  inicio: inicioGravacao = null,
  fim: fimGravacao = null,
} = {}) {
  // Respeita as quebras de trecho: o segmento de uma pausa (ou de um veículo)
  // não é distância percorrida. Ver `quebraAntes`.
  const distancia = distanciaTotal(pontos);

  // `> 0` e não só `finito`: gravação que nunca começou traz 0, e 0 aqui viraria
  // "1970" — um decorrido de 56 anos em cima de uma corrida de 20 minutos.
  const relogio = (v) => (finito(v) && num(v) > 0 ? num(v) : null);
  const inicio = relogio(inicioGravacao) ?? (pontos.length ? num(pontos[0].t) : 0);
  const fim = relogio(fimGravacao) ?? (pontos.length ? num(pontos[pontos.length - 1].t) : 0);
  const decorrido = Math.max(0, Math.round((fim - inicio) / 1000));
  const duracaoS = Math.max(0, decorrido - Math.round(num(pausaS)));

  const { pos, neg } = elevacao(pontos);

  return {
    distanciaM: Math.round(distancia),
    duracaoS,
    decorridoS: decorrido,
    paceMedioS: paceMedio(distancia, duracaoS),
    velocidadeKmh: velocidadeKmh(distancia, duracaoS),
    elevacaoPos: pos,
    elevacaoNeg: neg,
    passos: estimarPassos(modalidadeId, distancia),
    splits: calcularSplits(pontos),
    inicio,
    fim,
    pesoKg,
  };
}

const geo = {
  finito,
  haversine,
  quebraAntes,
  distanciaTotal,
  aceitarPonto,
  paceMovel,
  paceMedio,
  formatarPace,
  formatarTempo,
  velocidadeKmh,
  elevacao,
  suavizarAltitude,
  calorias,
  estimarPassos,
  calcularSplits,
  resumir,
  DESCARTE,
};

export default geo;
